import { useEffect, useState, useRef, memo } from "react";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import PatientInfo from "../../components/patient-settings/PatientInfo";
import AdditionalInfo from "../../components/patient-settings/AdditionalInfo";
import DataBounds from "../../components/patient-settings/DataBounds";
import Statuses from "../../components/patient-settings/Statuses";
import Notes from "../../components/patient-settings/Notes";
import Button from "../../components/button-v1";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updatePatientInfo,
} from "../../services/patient/settings";
import { getPatientGeneralDetails } from "../../services/patient/details";
import Spinner from "../../components/spinner";
import Reload from "../../components/ui/reload-data";
import { toast } from "react-hot-toast";
import { deactivatePatient } from "../../services/patient/settings";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { parseDateSafe } from "../../utils/dateFormatters";

const PatientSettings = () => {
  const navigate = useNavigate();
  const patientId = sessionStorage.getItem("_pt_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const dataBoundsRef = useRef();

  const queryClient = useQueryClient();
  const currentDate = new Date();
  const [selectedDay, setSelectedDay] = useState(currentDate.getDate());
  const [selectedMonth, setSelectedMonth] = useState(
    currentDate.getMonth() + 1
  );
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  // Generate options for days, months, and years
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from(
    { length: 101 },
    (_, i) => currentDate.getFullYear() - i
  );

  const [dataBounds, setDataBounds] = useState("");
  const [rpmCodes, setRpmCodes] = useState("");
  const [ccmCodes, setCcmCodes] = useState("");

  const [formError, setFormError] = useState({});
  const [formData, setFormData] = useState({
    resume: null,
    first_name: "",
    last_name: "",
    email: "",
    gender: "",
    phone: "",
    primary_phone: "",
    secondary_phone: "",
    tertiary_phone: "",
    provider: "",
    case_manager: "",
    dob: "",
    created_at: "",
    rpm_enrolled_at: "", 
    ccm_enrolled_at: "", 
    rpm: true,
    ccm: false,
    vital_sms_alert: false,
    rpm_codes: [],
    ccm_codes: [],

    username: "",
    password: "",
    confirm_password: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    zipcode: "",
    state: "",
    diagnosis_icd_10: "",
    secondary_provider: "",
    postal_code: "",
    serial_no: "",
    device_type: "",
  });

  const [isSectionOpen, setIsSectionOpen] = useState({
    sec1: true,
    sec2: false,
    sec3: false,
    sec4: false,
    sec5: false,
  });

  const {
    data: patientInfo,
    isLoading: isLoadingPatientInfo,
    error: errorPatientInfo,
    refetch: refetchPatientInfo,
  } = useQuery(["patient-general-details", patientId], () => getPatientGeneralDetails(patientId), {
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000,
  });

  const deactivatePatientMutation = useMutation(
    (body) => deactivatePatient({ patientId, body }),
    {
      onSuccess: () => {
        // queryClient.invalidateQueries(["patientInfo"]);
        toast.success("Patient deactivated successfully");
        window.location.replace("/dashboard");
      },
      onError: () => {
        toast.error("Could not deactivate patient");
      },
    }
  );

  const { mutate: updatePatient, isLoading: isLoadingUpdatePatient } =
    useMutation((body) => updatePatientInfo(body, patientId, locationId), {
      onSuccess: () => {
        [
          ["patient-general-details"],
          ["patient-readings"],
          ["my-tasks"],
        ].forEach((key) =>
          queryClient.invalidateQueries({ queryKey: key, exact: false })
        );
        toast.success("Patient info updated successfully");
        navigate(
          `/patient/details?patient_id=${patientId}&CCM=${+!!formData?.ccm}&RPM=${+!!formData?.rpm}`
        );
      },
      onError: (error) => {
        const response = error?.response?.data;
        // get first key
        const key = Object.keys(response)[0];
        const value = response[key];

        let message;

        if (Array.isArray(value)) {
          // join if multiple strings
          message = value.join(", ");
        } else {
          // if it's just a string
          message = value;
        }

        toast.error(message || "Could not update patient info.");
      },
    });

  const toggleSection = (section) => {
    setIsSectionOpen((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (formError[name]) {
      setFormError((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;

    // Handle CCM logic
    if (name === "ccm") {
      setFormData((prevState) => ({
        ...prevState,
        ccm: checked,
        rpm: !checked && !prevState.rpm ? true : prevState.rpm,
      }));
    } else {
      // Default checkbox behavior
      setFormData((prevState) => ({
        ...prevState,
        [name]: checked,
      }));
    }
  };

  // Helper to format ISO date strings for API submission
  const formatIsoDateForApi = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T01:00:00.000Z`;
    } else if (typeof dateValue === "string") {
      // If it's a string in MM/DD/YYYY format or YYYY-MM-DD
      const separator = dateValue.includes("/") ? "/" : "-";
      const parts = dateValue.split(separator);
      
      let year, month, day;
      if (separator === "/") {
        // MM/DD/YYYY
        year = parts[2];
        month = parts[0].padStart(2, "0");
        day = parts[1].padStart(2, "0");
      } else {
        // YYYY-MM-DD
        year = parts[0];
        month = parts[1].padStart(2, "0");
        day = parts[2].padStart(2, "0");
      }
      return `${year}-${month}-${day}T01:00:00.000Z`;
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // validate first_name
    if (!formData.first_name || formData.first_name.trim() === "") {
      const el = document.getElementById("first_name");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return setFormError((prev) => ({
        ...prev,
        first_name: "First name is required",
      }));
    }

    // validate last_name
    if (!formData.last_name || formData.last_name.trim() === "") {
      const el = document.getElementById("last_name");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return setFormError((prev) => ({
        ...prev,
        last_name: "Last name is required",
      }));
    }

    // validate password
    if (formData.password || formData.confirm_password) {
      const el = document.getElementById("confirm_password");

      if (formData.password.length < 8) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return setFormError((prev) => ({
          ...prev,
          password: "Must be 8 characters long",
        }));
      }

      if (formData.confirm_password < 8) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return setFormError((prev) => ({
          ...prev,
          confirm_password: "Must be 8 characters long",
        }));
      }
      if (formData.confirm_password !== formData.password) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return setFormError((prev) => ({
          ...prev,
          confirm_password: "Password do not match",
        }));
      }
    }

    // validate primary_phone (optional field)
    if (
      formData.primary_phone &&
      formData.primary_phone !== "" &&
      formData.primary_phone !== 0 &&
      formData.primary_phone !== "-1" &&
      formData.primary_phone.toString().length > 20
    ) {
      const el = document.getElementById("primary_phone");
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      return setFormError((prev) => ({
        ...prev,
        primary_phone: "Primary phone must not exceed 20 characters",
      }));
    }

    // validate email (required and proper format)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || formData.email.trim() === "") {
      const el = document.getElementById("email");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return setFormError((prev) => ({
        ...prev,
        email: "Email is required",
      }));
    }
    if (!emailRegex.test(formData.email)) {
      const el = document.getElementById("email");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return setFormError((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
    }

    // Get validated data bounds
    const validatedDataBounds = await dataBoundsRef.current.getValidatedData();

    if (!validatedDataBounds) {
      toast.error(
        "Please fix the validation errors in the Data Bounds section"
      );
      return;
    }

    // Clean primary_phone value - convert invalid values to empty string
    const cleanPrimaryPhone =
      !formData.primary_phone ||
      formData.primary_phone === "" ||
      formData.primary_phone === 0 ||
      formData.primary_phone === "-1"
        ? ""
        : formData.primary_phone;

    // Helper function to format date without timezone conversion
    const formatDateForAPI = (date) => {
      if (!date) return null;

      let d;
      if (typeof date === "string") {
        // If it's a string in MM/DD/YYYY format, parse it correctly
        if (date.includes("/")) {
          const [month, day, year] = date.split("/");
          d = new Date(year, month - 1, day); // month is 0-indexed
        } else {
          d = parseDateSafe(date) || new Date(date);
        }
      } else {
        d = parseDateSafe(date) || new Date(date);
      }

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const result = `${year}-${month}-${day}`;

      return result;
    };

    const body = {
      ...formData,
      dob: formatDateForAPI(formData.dob),
      created_at: formatIsoDateForApi(formData.created_at),
      rpm_enrolled_at: formatIsoDateForApi(formData.rpm_enrolled_at),
      ccm_enrolled_at: formatIsoDateForApi(formData.ccm_enrolled_at),
      user: {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        gender: formData.gender,
        primary_phone: cleanPrimaryPhone,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
        dob: formatDateForAPI(formData.dob),

        username: formData.username,
        password: formData.password,
      },
      // added short-circuit evaluation in case of null values
      rpm_codes: formData.rpm_codes || [],
      ccm_codes: formData.ccm_codes || [],
      data_bounds: validatedDataBounds,
      place_of_service_detail: undefined,
    };

    updatePatient(body);
    // location.href = '/patient'
  };

  const handleBlur = (e) => {
    const name = e?.target?.name ?? e?.name;
    const value = e?.target?.value ?? e?.value;

    if (
      (name === "first_name" || name === "last_name" || name === "email") &&
      (!value || value.trim() === "")
    ) {
      const requiredMessages = {
        first_name: "First name is required",
        last_name: "Last name is required",
        email: "Email is required",
      };
      setFormError((prev) => ({
        ...prev,
        [name]: requiredMessages[name],
      }));
      return;
    }

    // Password validation

    if (!value) {
      setFormError((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
      return;
    }
    if (name === "first_name" || name === "last_name") {
      setFormError((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    }
    if (name === "password" || name === "confirm_password") {
      if (value.length < 8) {
        setFormError((prev) => ({
          ...prev,
          [name]: "Password must be at least 8 characters long",
        }));
      } else {
        setFormError((prev) => {
          const { password, ...rest } = prev;
          return rest;
        });
      }
    }
    if (name === "confirm_password") {
      if (value !== formData.password) {
        setFormError((prev) => ({
          ...prev,
          confirm_password: "Passwords do not match",
        }));
      } else {
        setFormError((prev) => {
          const { confirm_password, ...rest } = prev;
          return rest;
        });
      }
    }
    if (name === "primary_phone") {
      if (value && value !== "-1" && value.toString().length > 20) {
        setFormError((prev) => ({
          ...prev,
          [name]: "Primary phone must not exceed 20 characters",
        }));
      } else {
        setFormError((prev) => {
          const { [name]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setFormError((prev) => ({
          ...prev,
          email: "Please enter a valid email address",
        }));
      } else {
        setFormError((prev) => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  useEffect(() => {
    const schema = [
      { name: "password", value: formData.password },
      { name: "confirm_password", value: formData.confirm_password },
      { name: "primary_phone", value: formData.primary_phone },
      { name: "email", value: formData.email },
    ];
    if (Array.isArray(schema)) {
      schema.forEach(({ name, value }) => {
        handleBlur({ name, value });
      });
      return;
    }
  }, [formData]);

  const parseApiDate = (dateString) => {
    if (!dateString) return "";
    let cleanDate = dateString.split("T")[0]; 
    let date;
    
    if (cleanDate.includes("-") && cleanDate.length === 10) {
      const [year, month, day] = cleanDate.split("-");
      date = new Date(year, month - 1, day);
    } else if (cleanDate.includes("/")) {
      date = parseDateSafe(cleanDate) || new Date();
    } else {
      date = parseDateSafe(cleanDate) || new Date();
    }

    const formatted = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
    return formatted;
  };

  useEffect(() => {
    if (patientInfo) {
      const user = patientInfo.user;

      setFormData((prevState) => ({
        ...prevState,
        ...user,
        dob: user.dob ? parseApiDate(user.dob) : "",
        ...patientInfo,
        created_at: patientInfo.created_at ? parseApiDate(patientInfo.created_at) : "",
        rpm_enrolled_at: patientInfo.rpm_enrolled_at ? parseApiDate(patientInfo.rpm_enrolled_at) : "",
        ccm_enrolled_at: patientInfo.ccm_enrolled_at ? parseApiDate(patientInfo.ccm_enrolled_at) : "",
        case_manager: patientInfo?.case_manager?.id,
        provider: patientInfo?.provider?.id,
        nurse_id: patientInfo?.nurse?.id,
        vital_sms_alert: patientInfo?.vital_sms_alert ?? false,
        // The API returns { id, name, is_location_default } but takes an id
        place_of_service: patientInfo?.place_of_service?.id ?? null,
        place_of_service_detail: patientInfo?.place_of_service ?? null,
      }));
    }
  }, [patientInfo, isLoadingPatientInfo]);

  useEffect(() => {
    if (patientInfo) {
      setDataBounds(patientInfo.data_bounds);
    }
  }, [patientInfo, isLoadingPatientInfo]);

  useEffect(() => {
    if (patientInfo) {
      setRpmCodes(patientInfo.rpm_codes);
      setCcmCodes(patientInfo.ccm_codes);
    }
  }, [patientInfo, isLoadingPatientInfo]);

  if (isLoadingPatientInfo) {
    return (
      <section className="content-center">
        <Spinner />;
      </section>
    );
  }

  if (errorPatientInfo) {
    return (
      <Reload message="Could not load data" onReload={refetchPatientInfo} />
    );
  }

  return (
    <div className={styles.patient_page}>
      <div className={styles.monitoring_time_container}>
        <div className={styles.form_container}>
          <section className={styles.form_wrapper}>
            <PatientInfo
              formData={formData}
              handleChange={handleChange}
              handleCheckBoxChange={handleCheckBoxChange}
              isOpen={isSectionOpen.sec1}
              toggleSection={toggleSection}
              selectedDay={selectedDay}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              setSelectedDay={setSelectedDay}
              setSelectedMonth={setSelectedMonth}
              setSelectedYear={setSelectedYear}
              days={days}
              months={months}
              years={years}
              formError={formError}
              setFormError={setFormError}
              handleBlur={handleBlur}
            />

            <AdditionalInfo
              formData={formData}
              rpmCodeInfo={rpmCodes}
              ccmCodeInfo={ccmCodes}
              handleChange={handleChange}
              isOpen={isSectionOpen.sec2}
              toggleSection={toggleSection}
            />

            <DataBounds
              ref={dataBoundsRef}
              data_bounds={dataBounds}
              isOpen={isSectionOpen.sec4}
              toggleSection={toggleSection}
            />

            <Statuses
              isOpen={isSectionOpen.sec3}
              toggleSection={toggleSection}
            />

            {/* Add the new Notes component */}
            <Notes isOpen={isSectionOpen.sec5} toggleSection={toggleSection} />

            <section className={styles.action_buttons}>
              <section></section>
              <Button
                variant="primary"
                size="default"
                loading={isLoadingUpdatePatient}
                onClick={handleSubmit}
              >
                Save
              </Button>

              <Button
                variant="danger"
                onClick={() => setShowDeactivateModal(true)}
              >
                Deactivate
              </Button>
            </section>

            {/* Deactivate Patient Modal */}
            <Dialog
              open={showDeactivateModal}
              setOpen={() => setShowDeactivateModal(false)}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Deactivate Patient</DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <div className={styles.content}>
                    <textarea
                      value={deactivateReason}
                      onChange={(e) => setDeactivateReason(e.target.value)}
                      className={styles.reason_textarea}
                      autoFocus
                      placeholder="Reason"
                    />
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={() => setShowDeactivateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    loading={deactivatePatientMutation.isLoading}
                    disabled={deactivatePatientMutation.isLoading}
                    variant="danger"
                    onClick={() => {
                      if (!deactivateReason) {
                        return toast.error(
                          "Please provide a reason to deactivate patient"
                        );
                      }
                      deactivatePatientMutation.mutate({
                        user: {
                          is_active: false,
                          deactivation_reason: deactivateReason,
                        },
                      });
                    }}
                  >
                    Deactivate
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </section>
        </div>
      </div>
    </div>
  );
};

export default memo(PatientSettings);